
import React, { useState, useEffect, useRef, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import TemplatePicker from "../../components/TemplatePicker";

// ── WhatsApp Centre ──────────────────────────────────────────
// Three panels: Session | Templates | Blast
export default function WhatsAppCenter({ user }) {
  const [panel, setPanel]       = useState("session");
  const [status, setStatus]     = useState("not_started");
  const [phone, setPhone]       = useState(null);
  const [qr, setQr]             = useState(null);
  const [polling, setPolling]   = useState(false);

  // Templates
  const [templates, setTemplates]   = useState([]);
  const [tmplEdit, setTmplEdit]     = useState(null); // null = new
  const [tmplName, setTmplName]     = useState("");
  const [tmplBody, setTmplBody]     = useState("");

  // Blast
  const [leads, setLeads]           = useState([]);
  const [selectedLeads, setSelected] = useState(new Set());
  const [blastMsg, setBlastMsg]           = useState("");
  const [blastTmpl, setBlastTmpl]         = useState("");
  const [blasting, setBlasting]           = useState(false);
  const [showWaPicker, setShowWaPicker]   = useState(false);
  const [blastResults, setBlastResults] = useState(null);
  const [filterQ, setFilterQ]       = useState("");
  const [failureReason, setFailureReason] = useState(null);

  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";
  const pollRef = useRef(null);
  const qrRef   = useRef(null); // avoid stale closure: track current QR without adding it as a dep

  // ── Poll session status ──────────────────────────────────
  // IMPORTANT: empty deps [] — uses qrRef to compare QRs so the interval never
  // resets when a new QR image arrives (old bug: [qr] caused interval to restart).
  const pollStatus = useCallback(async () => {
    try {
      const res = await API.get("/whatsapp/session/status");
      const d   = res.data;
      setStatus(d.status);
      if (d.phone) setPhone(d.phone);
      setFailureReason(d.reason || null);

      if (d.status === "qr_ready") {
        // Always fetch latest QR — codes expire in 20-60s. Use ref to avoid
        // redundant re-renders when the same QR is returned.
        const qrRes = await API.get("/whatsapp/session/qr");
        const freshQr = qrRes.data.qr;
        if (freshQr && freshQr !== qrRef.current) {
          qrRef.current = freshQr;
          setQr(freshQr);
        }
      }
      if (d.status === "connected") {
        setQr(null);
        qrRef.current = null;
        clearInterval(pollRef.current);
        setPolling(false);
      }
    } catch {}
  }, []);

  // On mount: check status once. If already pending/qr_ready (e.g. page refresh),
  // auto-start polling so the user doesn't have to click "Start WhatsApp" again.
  useEffect(() => {
    const init = async () => {
      try {
        const res = await API.get("/whatsapp/session/status");
        const d   = res.data;
        setStatus(d.status);
        if (d.phone) setPhone(d.phone);
        setFailureReason(d.reason || null);
        if (d.status === "qr_ready") {
          const qrRes = await API.get("/whatsapp/session/qr");
          qrRef.current = qrRes.data.qr;
          setQr(qrRes.data.qr);
        }
        if (["pending", "qr_ready"].includes(d.status)) {
          setPolling(true); // resume polling automatically
        }
      } catch {}
    };
    init();
  }, []);

  useEffect(() => {
    if (polling) {
      pollRef.current = setInterval(pollStatus, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [polling, pollStatus]);

  const startSession = async () => {
    try {
      setStatus("pending");
      setQr(null);
      qrRef.current = null;
      setPolling(true);
      await API.post("/whatsapp/session/start");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to start session");
      setStatus("not_started");
      setPolling(false);
    }
  };

  // Force-kill the old session + wipe auth folder, then boot fresh.
  // Fixes "Couldn't link device" caused by stale/corrupted auth data.
  const restartSession = async () => {
    try {
      clearInterval(pollRef.current);
      setPolling(false);
      setStatus("pending");
      setQr(null);
      qrRef.current = null;
      toast("Restarting — generating fresh QR...", { icon: "🔄" });
      await API.post("/whatsapp/session/restart");
      setPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.error || "Restart failed");
      setStatus("not_started");
    }
  };

  const logout = async () => {
    clearInterval(pollRef.current);
    setPolling(false);
    await API.post("/whatsapp/session/logout");
    setStatus("not_started");
    setPhone(null);
    setQr(null);
    qrRef.current = null;
    toast.success("WhatsApp disconnected");
  };

  // ── Load templates ───────────────────────────────────────
  useEffect(() => {
    if (panel === "templates" || panel === "blast") {
      API.get("/whatsapp/templates").then(r => setTemplates(r.data.templates || [])).catch(() => {});
    }
    if (panel === "blast") {
      API.get("/leads").then(r => setLeads(r.data.leads || r.data || [])).catch(() => {});
    }
  }, [panel]);

  const saveTemplate = async () => {
    if (!tmplName.trim() || !tmplBody.trim()) return toast.error("Name and body required");
    try {
      if (tmplEdit) {
        await API.put(`/whatsapp/templates/${tmplEdit.id}`, { name: tmplName, body: tmplBody });
        toast.success("Template updated");
      } else {
        await API.post("/whatsapp/templates", { name: tmplName, body: tmplBody });
        toast.success("Template created");
      }
      setTmplEdit(null); setTmplName(""); setTmplBody("");
      API.get("/whatsapp/templates").then(r => setTemplates(r.data.templates || []));
    } catch (e) { toast.error(e.response?.data?.error || "Save failed"); }
  };

  const deleteTemplate = async (id) => {
    await API.delete(`/whatsapp/templates/${id}`);
    setTemplates(t => t.filter(x => x.id !== id));
    toast.success("Template deleted");
  };

  // ── Blast ────────────────────────────────────────────────
  const toggleLead = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const runBlast = async () => {
    if (!blastMsg.trim()) return toast.error("Message is required");
    if (!selectedLeads.size)  return toast.error("Select at least one lead");
    setBlasting(true);
    setBlastResults(null);
    try {
      const res = await API.post("/whatsapp/blast", {
        lead_ids:    Array.from(selectedLeads),
        message:     blastMsg.trim(),
        template_id: blastTmpl || null,
        delay_ms:    5000,
      });
      setBlastResults(res.data);
      toast.success(`Sent: ${res.data.summary.sent} | Failed: ${res.data.summary.failed}`);
    } catch (e) { toast.error("Blast failed"); }
    setBlasting(false);
  };

  const filteredLeads = leads.filter(l =>
    !filterQ || l.institution_name?.toLowerCase().includes(filterQ.toLowerCase()) ||
    l.contact_name?.toLowerCase().includes(filterQ.toLowerCase())
  );

  const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 20, marginBottom: 16 };
  const btn  = (active, extra = {}) => ({
    padding: "9px 20px", border: "none", borderRadius: 20,
    background: active ? accent : "#f1f5f9",
    color: active ? "#fff" : "#64748b",
    fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
    ...extra,
  });

  return (
    <>
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 28 }}>💬</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>WhatsApp Centre</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              Status: <strong style={{ color: status === "connected" ? "#16a34a" : status === "qr_ready" ? "#f59e0b" : status === "failed" ? "#dc2626" : "#94a3b8" }}>
                {status === "connected"   ? `Connected (+${phone})`
                 : status === "qr_ready"  ? "Scan QR Code"
                 : status === "pending" || status === "starting" ? "Starting up..."
                 : status === "failed"    ? "Connection failed — restart"
                 : status === "disconnected" ? "Disconnected"
                 : "Not connected"}
              </strong>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["session","templates","blast"].map(p => (
            <button key={p} style={btn(panel === p)} onClick={() => setPanel(p)}>
              {p === "session" ? "📱 Session" : p === "templates" ? "📝 Templates" : "🚀 Blast"}
            </button>
          ))}
        </div>
      </div>

      {/* ── SESSION PANEL ── */}
      {panel === "session" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>Connect Your WhatsApp</h3>

          {status === "not_started" && (
            <>
              <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>
                Connect your personal WhatsApp number to send messages directly from the CRM. 
                Your number stays private — messages appear from your own WhatsApp.
              </p>
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: 14, marginBottom: 20 }}>
                <strong style={{ color: "#15803d", fontSize: 13 }}>How it works:</strong>
                <ol style={{ margin: "8px 0 0", paddingLeft: 18, color: "#166534", fontSize: 13, lineHeight: 2 }}>
                  <li>Click "Start WhatsApp" below</li>
                  <li>A QR code appears — scan it with your WhatsApp mobile app</li>
                  <li>Go to WhatsApp → Linked Devices → Link a Device</li>
                  <li>Done! Your number is now connected</li>
                </ol>
              </div>
              <button onClick={startSession}
                style={{ padding: "12px 28px", background: accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                Start WhatsApp
              </button>
            </>
          )}

          {(status === "pending" || status === "starting" || status === "qr_ready") && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              {!qr ? (
                <div>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
                  <div style={{ color: "#64748b", fontWeight: 700, fontSize: 15 }}>Starting WhatsApp...</div>
                  <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 8 }}>
                    This can take up to 30 seconds — please wait for the QR to appear.
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 6 }}>
                    Do not click Start again — that will interrupt the connection.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: "inline-block", padding: 12, background: "#fff", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.10)", border: "2px solid #e2e8f0" }}>
                    <img src={qr} alt="WhatsApp QR" style={{ width: 230, height: 230, display: "block" }} />
                  </div>
                  <div style={{ marginTop: 16, color: "#1e293b", fontSize: 14, fontWeight: 700 }}>
                    📱 Open WhatsApp on your phone
                  </div>
                  <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>
                    Tap <strong>⋮ Menu → Linked Devices → Link a Device</strong> → Scan this code
                  </div>
                  <div style={{ marginTop: 10, color: "#94a3b8", fontSize: 12 }}>
                    QR refreshes automatically if not scanned within 60 seconds.
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <button onClick={restartSession}
                      style={{ padding: "8px 20px", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
                      🔄 Get a fresh QR
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === "connected" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#15803d" }}>WhatsApp Connected!</div>
              <div style={{ color: "#64748b", marginTop: 6 }}>Number: <strong>+{phone}</strong></div>
              <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 8, marginBottom: 20 }}>
                Messages sent from CRM will appear from your personal WhatsApp
              </div>
              <button onClick={logout}
                style={{ padding: "10px 24px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                Disconnect WhatsApp
              </button>
            </div>
          )}

          {status === "failed" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>❌</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#dc2626", marginBottom: 8 }}>Connection failed</div>
              {/* The old copy promised a restart "always fixes it". It does not,
                  and saying so sent people round the same loop. Show what
                  WhatsApp actually reported, then say honestly what a restart
                  can and cannot do. */}
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "12px 18px", marginBottom: 20, fontSize: 13, color: "#7f1d1d", maxWidth: 420, margin: "0 auto 20px", textAlign: "left" }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>WhatsApp would not link this device.</div>
                {failureReason && (
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, background: "#fff", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 8px", margin: "0 0 8px", wordBreak: "break-word" }}>
                    {failureReason}
                  </div>
                )}
                <div style={{ marginBottom: 6 }}>
                  A restart clears this device’s saved session and issues a new code. That helps
                  when the code simply expired before it was scanned — they last under a minute.
                </div>
                <div>
                  If it keeps failing, the cause is on WhatsApp’s side rather than yours: linked-device
                  slots are full (check <strong>WhatsApp › Linked devices</strong> on the phone), or WhatsApp
                  has shipped a web update this integration has not caught up with. Restarting will not fix either.
                </div>
              </div>
              <button onClick={restartSession}
                style={{ padding: "12px 28px", background: accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                🔄 Restart &amp; Get Fresh QR
              </button>
            </div>
          )}

          {status === "disconnected" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📵</div>
              <div style={{ color: "#64748b", fontWeight: 600, marginBottom: 16 }}>
                WhatsApp session was disconnected.
              </div>
              <button onClick={restartSession}
                style={{ padding: "12px 28px", background: accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                Reconnect WhatsApp
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── TEMPLATES PANEL ── */}
      {panel === "templates" && (
        <div>
          {/* Editor */}
          <div style={card}>
            <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>{tmplEdit ? "Edit Template" : "New Template"}</h3>
            <div style={{ display: "grid", gap: 12 }}>
              <input placeholder="Template name (e.g. Follow-up Day 1)"
                value={tmplName}
                onChange={e => setTmplName(e.target.value)}
                style={{ padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none" }}
              />
              <textarea placeholder={"Message body. Use {{name}} for lead name.\nExample: Hi {{name}}, following up on our visit last week..."}
                value={tmplBody}
                onChange={e => setTmplBody(e.target.value)}
                rows={5}
                style={{ padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none" }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveTemplate}
                  style={{ padding: "10px 24px", background: accent, color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {tmplEdit ? "Update" : "Save Template"}
                </button>
                {tmplEdit && (
                  <button onClick={() => { setTmplEdit(null); setTmplName(""); setTmplBody(""); }}
                    style={{ padding: "10px 16px", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* List */}
          <div style={card}>
            <h3 style={{ margin: "0 0 14px", color: "#1e293b" }}>Saved Templates ({templates.length})</h3>
            {templates.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>No templates yet. Create your first one above.</div>}
            {templates.map(t => (
              <div key={t.id} style={{ padding: "12px 14px", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 10, background: "#f8fafc" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>{t.name}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => { setTmplEdit(t); setTmplName(t.name); setTmplBody(t.body); }}
                      style={{ padding: "4px 10px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                      Edit
                    </button>
                    <button onClick={() => deleteTemplate(t.id)}
                      style={{ padding: "4px 10px", border: "1px solid #fecaca", borderRadius: 7, background: "#fff", color: "#dc2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                      Delete
                    </button>
                  </div>
                </div>
                <div style={{ color: "#64748b", fontSize: 12, marginTop: 6, whiteSpace: "pre-wrap" }}>{t.body.slice(0, 120)}{t.body.length > 120 ? "..." : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── BLAST PANEL ── */}
      {panel === "blast" && (
        <div>
          {/* Compose */}
          <div style={card}>
            <h3 style={{ margin: "0 0 14px", color: "#1e293b" }}>Compose Blast Message</h3>

            {status !== "connected" && (
              <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13, color: "#92400e" }}>
                ⚠️ WhatsApp not connected — messages will be sent via Business API.{" "}
                <button onClick={() => setPanel("session")} style={{ background: "none", border: "none", color: "#92400e", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
                  Connect personal number
                </button>
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <button onClick={() => setShowWaPicker(true)}
                style={{ padding: "9px 16px", background: "#dcfce7", border: "none", borderRadius: 10,
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", color: "#166534" }}>
                💬 Use Template
              </button>
            </div>

            <textarea
              value={blastMsg}
              onChange={e => setBlastMsg(e.target.value)}
              placeholder={"Type your message. Use {{name}} to personalise.\nExample: Hi {{name}}, we visited your institution last week..."}
              rows={5}
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }}
            />
            <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 6 }}>
              📌 Each message is spaced ≥5 seconds apart · Selected: {selectedLeads.size} lead{selectedLeads.size !== 1 ? "s" : ""}
            </div>

            <button onClick={runBlast} disabled={blasting || !selectedLeads.size || !blastMsg.trim()}
              style={{ marginTop: 12, padding: "12px 28px", background: blasting ? "#94a3b8" : accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
              {blasting ? "⏳ Sending..." : `🚀 Send to ${selectedLeads.size} Lead${selectedLeads.size !== 1 ? "s" : ""}`}
            </button>
          </div>

          {/* Results */}
          {blastResults && (
            <div style={{ ...card, borderColor: "#bbf7d0" }}>
              <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                {[["Sent", blastResults.summary.sent, "#16a34a"], ["Failed", blastResults.summary.failed, "#dc2626"], ["Skipped", blastResults.summary.skipped, "#f59e0b"]].map(([l, v, c]) => (
                  <div key={l} style={{ textAlign: "center", flex: 1, background: "#f8fafc", borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 24, fontWeight: 900, color: c }}>{v}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lead picker */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: "#1e293b" }}>Select Leads</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setSelected(new Set(filteredLeads.map(l => l.id)))}
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#f8fafc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  Select All
                </button>
                <button onClick={() => setSelected(new Set())}
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#f8fafc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  Clear
                </button>
              </div>
            </div>
            <input placeholder="Filter by name..."
              value={filterQ}
              onChange={e => setFilterQ(e.target.value)}
              style={{ width: "100%", padding: "9px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, marginBottom: 12, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
            />
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {filteredLeads.map(l => (
                <label key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, cursor: "pointer", background: selectedLeads.has(l.id) ? "#f0fdf4" : "transparent", marginBottom: 2 }}>
                  <input type="checkbox" checked={selectedLeads.has(l.id)} onChange={() => toggleLead(l.id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{l.institution_name}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{l.contact_name} · {l.contact_phone || "No phone"}</div>
                  </div>
                  {!l.contact_phone && <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>NO PHONE</span>}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* WhatsApp Template Picker */}
    {showWaPicker && (
      <TemplatePicker
        type="whatsapp"
        accent={accent}
        vars={{ sender_name: user?.name || "", company_name: user?.company === "ebekmed" ? "EbekMed" : "Ebek" }}
        onSelect={({ body }) => {
          setBlastMsg(body);
          toast.success("Template loaded");
        }}
        onClose={() => setShowWaPicker(false)}
      />
    )}
    </>
  );
}
